import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { useDeleteValidationType } from 'src/features/validationTypes/api/useDeleteValidationType';
import { LIST_VALIDATION_TYPES_RESOURCE } from 'src/features/validationTypes/api/useListValidationTypes';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { ValidationType } from 'src/features/validationTypes/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface DeleteValidationTypeProps<TData> extends DataTableNewEntryProps<TData> {
    selectedType: ValidationType;
    children?: React.ReactNode;
}

function DeleteValidationType<TData>({ selectedType, children }: DeleteValidationTypeProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const onError = useOnError();

    const { mutate: deleteValidationType } = useDeleteValidationType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VALIDATION_TYPES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(validationTypesMessages.deleteValidationTypeSuccess),
                description: formatMessage(validationTypesMessages.deleteValidationTypeSuccessDescription, {
                    type: selectedType.name,
                }),
            });
        },
        onError: (error) => {
            onError(error, () => null, { inlineErrorStatuses: [] });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: {
                    ...validationTypesMessages.deleteValidationType,
                },
                subTitle: {
                    ...validationTypesMessages.deleteValidationTypeSubtitle,
                    values: {
                        type: selectedType.name,
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...validationTypesMessages.confirmDeleteValidationType,
                    values: {
                        type: selectedType.name,
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (selectedType) {
                        return deleteValidationType({
                            id: selectedType.id,
                            parkingId,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteValidationType;
